// What the client knows of its zone and the players in it (ClientCore/World: ClientWorld, ServerClock, RemoteEntity,
// LocalPlayer).
import { EntityFlags, EntityKind, Facing, GameConstants, InputButtons, ZoneFlags, inputFacingShift } from '../../../shared/src/constants';
import { ChunkMap, chunkOfPixel, chunkOfTile } from './map';
import { newMotorState, scale, step, type MotorState } from '../../../shared/src/motor';
import { tileEntityDef, TileEntities, type TileEntity } from '../../../shared/src/rules/world';
import type { ItemStack } from './rules/items';

/** One tick of player intent. */
export interface PlayerInput { sequence: number; buttons: number }

/**
 * A continuous estimate of the server's tick, driven by local time and gently corrected toward the ticks in
 * snapshots. Remote entities are drawn at renderTick, a little in the past.
 */
export class ServerClock {
  interpolationDelay = 3.5;
  estimatedTick = 0;
  initialized = false;

  get renderTick(): number { return this.estimatedTick - this.interpolationDelay; }
  reset(): void { this.initialized = false; }
  advance(dt: number): void { if (this.initialized) this.estimatedTick += dt * GameConstants.tickRate; }

  onServerTick(tick: number): void {
    if (!this.initialized) {
      this.estimatedTick = tick;
      this.initialized = true;
      return;
    }
    const error = tick - this.estimatedTick;
    if (Math.abs(error) > 15) this.estimatedTick = tick; // a big jump (hitch, resume): resync
    else this.estimatedTick += error * 0.08;
  }
}

interface Sample { tick: number; x: number; y: number; facing: Facing; flags: number }

/** Another player or a mob, drawn in the past by blending the two snapshots around the render time. */
export class RemoteEntity {
  private static readonly bufferSize = 32;
  private static readonly teleportDistance = 64;
  private readonly samples: Sample[] = [];

  level = 1;
  armor: number[] = [];
  /** Latest health from snapshots, 0-255. */
  healthPct = 255;
  /** Client time until which it flashes from a hit (the page's). */
  hitFlashUntil = 0;
  swingUntil = 0;
  swingFacing = Facing.Down;
  wasMoving = false;
  x: number;
  y: number;
  facing: Facing;
  moving = false;
  running = false;
  blocking = false;
  stunned = false;
  /** A ghost running back to its corpse. */
  ghost = false;
  animTime = 0;

  constructor(readonly id: number, readonly kind: EntityKind, readonly name: string, readonly appearance: number,
    x: number, y: number, facing: Facing, flags: number, tick: number) {
    this.x = x;
    this.y = y;
    this.facing = facing;
    this.addSample(tick, x, y, facing, flags);
  }

  /** A mob's type (its appearance byte), 0 for players. */
  get mobType(): number { return this.kind === EntityKind.Mob ? this.appearance : 0; }

  addSample(tick: number, x: number, y: number, facing: Facing, flags: number): void {
    const s = this.samples;
    if (s.length > 0 && tick <= s[s.length - 1].tick) return;
    s.push({ tick, x, y, facing, flags });
    if (s.length > RemoteEntity.bufferSize) s.shift();
  }

  update(renderTick: number, dt: number): void {
    const s = this.samples;
    if (s.length === 0) return;
    while (s.length > 2 && s[1].tick <= renderTick) s.shift(); // keep one older than the render time
    const a = s[0];
    if (s.length === 1 || renderTick <= a.tick) this.apply(a.x, a.y, a);
    else {
      const b = s[1];
      if (renderTick >= b.tick) {
        this.apply(b.x, b.y, b); // out of data: hold the latest
        if (renderTick - b.tick > 6) this.moving = false;
      } else if (Math.hypot(b.x - a.x, b.y - a.y) > RemoteEntity.teleportDistance) {
        this.apply(b.x, b.y, b);
      } else {
        const t = (renderTick - a.tick) / (b.tick - a.tick);
        this.apply(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, t < 0.5 ? a : b);
      }
    }
    this.animTime = this.moving ? this.animTime + dt : 0;
  }

  private apply(x: number, y: number, s: Sample): void {
    this.x = x;
    this.y = y;
    this.facing = s.facing;
    this.moving = (s.flags & EntityFlags.Moving) !== 0;
    this.running = (s.flags & EntityFlags.Running) !== 0;
    this.blocking = (s.flags & EntityFlags.Blocking) !== 0;
    this.stunned = (s.flags & EntityFlags.Stunned) !== 0;
    this.ghost = (s.flags & EntityFlags.Ghost) !== 0;
  }
}

/** The server's view of our own player, from a snapshot (exact integers). */
export interface SelfState { x: number; y: number; facing: Facing; flags: number; staminaUnits: number; staminaDelay: number; stunTicks: number }

/**
 * Client-side prediction: inputs run at once at the fixed tick rate through the same motor the server uses, and are
 * kept until acknowledged. Each snapshot rewinds to the server's state and replays the unacknowledged inputs; any
 * residual error is smoothed out rather than snapped.
 */
export class LocalPlayer {
  private static readonly maxPending = GameConstants.tickRate * 3;
  private static readonly snapDistance = 48;
  private readonly pending: PlayerInput[] = [];
  private current: MotorState = newMotorState(0, 0);
  private previous: MotorState = newMotorState(0, 0);
  private smoothX = 0;
  private smoothY = 0;
  private accumulator = 0;
  private nextSequence = 1;
  private lastAck = 0;
  private started = false;
  /** A facing for the next input to turn to (an attack's direction), or null. */
  private turnTo: Facing | null = null;

  entityId = 0;
  name = '';
  appearance = 0;
  corrections = 0;
  lastCorrectionDistance = 0;
  animTime = 0;
  /** Where to draw this frame, in pixels: between ticks, plus correction smoothing. */
  renderX = 0;
  renderY = 0;

  get state(): Readonly<MotorState> { return this.current; }
  get pendingInputs(): readonly PlayerInput[] { return this.pending; }
  /** Position in pixels. */
  get x(): number { return this.current.x / scale; }
  get y(): number { return this.current.y / scale; }

  /** Starts predicting from an exact position (1/256 px), as a zone change gives it. Stamina carries across zones. */
  reset(x: number, y: number, facing: Facing): void {
    const keep = this.started ? { staminaUnits: this.current.staminaUnits, staminaDelay: this.current.staminaDelay, exhausted: this.current.exhausted } : {};
    this.current = { ...newMotorState(x, y, facing), ...keep };
    this.previous = { ...this.current };
    this.started = true;
    this.pending.length = 0;
    this.turnTo = null;
    this.smoothX = this.smoothY = 0;
    this.accumulator = 0;
    this.renderX = this.x;
    this.renderY = this.y;
  }

  /** Turns to face a direction on the next tick (and stays so until moving turns it), as the server will. */
  face(facing: Facing): void { this.turnTo = facing; }

  /** Advances the fixed-step simulation; returns how many ticks ran. */
  update(dt: number, buttons: number, map: ChunkMap, send: (inputs: readonly PlayerInput[]) => void): number {
    this.accumulator = Math.min(this.accumulator + dt, 0.25); // no death spiral after a long hitch
    let ticks = 0;
    while (this.accumulator >= GameConstants.tickDelta) {
      this.accumulator -= GameConstants.tickDelta;
      ticks++;
      // Don't walk into terrain that hasn't arrived: stand still until it does.
      let b = map.has(chunkOfPixel(this.x), chunkOfPixel(this.y)) ? buttons : 0;
      if (this.turnTo !== null) { b |= InputButtons.Face | this.turnTo << inputFacingShift; this.turnTo = null; }
      const input = { sequence: this.nextSequence++, buttons: b };
      this.previous = { ...this.current };
      step(this.current, b, map);
      if (this.pending.length >= LocalPlayer.maxPending) this.pending.shift();
      this.pending.push(input);
      send(this.pending.length <= GameConstants.inputRedundancy ? this.pending : this.pending.slice(-GameConstants.inputRedundancy));
    }
    const alpha = this.accumulator / GameConstants.tickDelta;
    const decay = Math.exp(-dt * 14);
    this.smoothX *= decay;
    this.smoothY *= decay;
    if (this.smoothX * this.smoothX + this.smoothY * this.smoothY < 0.0025) this.smoothX = this.smoothY = 0;
    const px = this.previous.x / scale, py = this.previous.y / scale;
    this.renderX = px + (this.x - px) * alpha + this.smoothX;
    this.renderY = py + (this.y - py) * alpha + this.smoothY;
    this.animTime = this.current.moving ? this.animTime + dt : 0;
    return ticks;
  }

  /** Applies an authoritative snapshot of this player. */
  reconcile(lastProcessedInput: number, server: SelfState, map: ChunkMap): void {
    if (lastProcessedInput < this.lastAck) return; // stale
    this.lastAck = lastProcessedInput;
    let acked = 0;
    while (acked < this.pending.length && this.pending[acked].sequence <= lastProcessedInput) acked++;
    this.pending.splice(0, acked);

    const replay: MotorState = {
      ...this.current, x: server.x, y: server.y, facing: server.facing, staminaUnits: server.staminaUnits,
      staminaDelay: server.staminaDelay, exhausted: (server.flags & EntityFlags.Exhausted) !== 0, stunTicks: server.stunTicks,
    };
    for (const input of this.pending) step(replay, input.buttons, map);
    if (replay.x === this.current.x && replay.y === this.current.y) {
      // Position agrees; still adopt the authoritative stamina so the bar never drifts.
      this.current.facing = replay.facing;
      this.current.staminaUnits = replay.staminaUnits;
      this.current.staminaDelay = replay.staminaDelay;
      this.current.exhausted = replay.exhausted;
      this.current.stunTicks = replay.stunTicks;
      return;
    }
    const ex = (replay.x - this.current.x) / scale, ey = (replay.y - this.current.y) / scale;
    const dist = Math.hypot(ex, ey);
    this.corrections++;
    this.lastCorrectionDistance = dist;
    if (dist > LocalPlayer.snapDistance) {
      this.smoothX = this.smoothY = 0;
      this.previous = { ...replay };
    } else {
      // Keep the drawn position continuous, then let the offset decay.
      this.smoothX -= ex;
      this.smoothY -= ey;
      this.previous.x += replay.x - this.current.x;
      this.previous.y += replay.y - this.current.y;
    }
    this.current = replay;
  }
}

export interface LootBag { id: number; x: number; y: number; contents: ItemStack[] }

/** A point light placed on the map: world pixels, radius in pixels, colour 0xrrggbb. */
export interface MapLight { x: number; y: number; radius: number; color: number; flicker: boolean }

export interface ZoneInfo {
  epoch: number; zoneId: number; instanceId: number; zoneName: string; widthTiles: number; heightTiles: number; flags: ZoneFlags;
  lights: MapLight[];
}

/** The zone the client is in: streamed terrain and what's replicated in it. */
export class ClientWorld {
  map = new ChunkMap(0, 0);
  readonly entities = new Map<number, RemoteEntity>();
  readonly tileEntities = new Map<number, TileEntity>();
  readonly bags = new Map<number, LootBag>();
  epoch = 0;
  zoneId = 0;
  instanceId = 0;
  zoneName = '';
  flags = ZoneFlags.None;
  /** The map's own point lights (never change while in the zone). */
  lights: MapLight[] = [];
  hasZone = false;

  get isIndoors(): boolean { return (this.flags & ZoneFlags.Indoors) !== 0; }
  get isDark(): boolean { return (this.flags & ZoneFlags.Dark) !== 0; }

  enterZone(z: ZoneInfo): void {
    this.epoch = z.epoch;
    this.zoneId = z.zoneId;
    this.instanceId = z.instanceId;
    this.zoneName = z.zoneName;
    this.flags = z.flags;
    this.lights = z.lights;
    this.map = new ChunkMap(z.widthTiles, z.heightTiles);
    this.entities.clear();
    this.tileEntities.clear();
    this.bags.clear();
    this.hasZone = true;
  }

  addTileEntity(e: TileEntity): void {
    this.removeTileEntity(e.id);
    this.tileEntities.set(e.id, e);
    if (tileEntityDef(e.type)?.solid) for (const [x, y] of TileEntities.footprint(e)) this.map.blockers.add(`${x},${y}`);
  }

  removeTileEntity(id: number): void {
    const e = this.tileEntities.get(id);
    if (!e) return;
    this.tileEntities.delete(id);
    if (tileEntityDef(e.type)?.solid) for (const [x, y] of TileEntities.footprint(e)) this.map.blockers.delete(`${x},${y}`);
  }

  /** Tile entities and bags travel with their anchor chunk, so they go when it unloads. */
  removeInChunk(cx: number, cy: number): void {
    for (const b of [...this.bags.values()]) if (chunkOfPixel(b.x) === cx && chunkOfPixel(b.y) === cy) this.bags.delete(b.id);
    for (const e of [...this.tileEntities.values()]) if (chunkOfTile(e.x) === cx && chunkOfTile(e.y) === cy) this.removeTileEntity(e.id);
  }
}
